import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  TextInput,
  InputSwitch,
  isValidate,
  isFromValid,
  DescriptionTextArea,
} from 'smart-react';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import {
  buildNotification,
  GenerateNotification,
  DropDownInput,
} from 'smart-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createIntegrationConnection,
  updateIntegrationConnection,
  deleteIntegrationConnection,
} from '../Services/IntegrationConnectionService';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { Error } from '@progress/kendo-react-labels';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import TenantDropdown from '../../../Core/Components/TenantDropdown/TenantDropdown';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from '../../../../Utils/Auth/ValidatePermissions';
import { getIntegrationConnection } from '../Services/IntegrationConnectionService';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import { listConnectionTypes } from '../../../Maintenance/ConnectionType/Services/ConnectionTypeService';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
  DELETE_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Integration Connection main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Integration Connection component.
 */
const AddIntegrationConnection = () => {
  const navigate = useNavigate();
  const { tenantID } = useAuth(); // Get tenantID using Auth Context
  const { tenantId } = useParams();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const integrationSystem = getParam('integrationSystem');
  /**
   * Used to handle multiple tabs
   */
  const [selected, setSelected] = React.useState(getParam('userCredential') ? 1 : 0);
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const { state } = useLocation();
  const [tempState, setTempState] = React.useState(state);
  const [visible, setVisible] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);

  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [system, setSystem] = React.useState({});
  const [tenant, setTenant] = React.useState(findTenantById(system?.TenantId));
  const [loading, setLoading] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [formData, setFormData] = React.useState({
    TenantId: system?.TenantId,
    TenantName: tenant?.TenantName,
    ConnectionId: '',
    SystemConnTypeId: '',
    ConnectionTypeName: '',
    IntegrationId: system?.IntegrationId,
    SysType: system?.SysType,
    Description: '',
    AuthEndpointUrl: '',
    ApiEndpointUrl: '',
    Active: true,
  });
  const [connectionTypes, setConnectionTypes] = React.useState([]);
  const [editorField, setEditorField] = React.useState({
    name: '',
    value: '',
    label: '',
  });
  const [validateForm, setValidateForm] = React.useState(false);
  const requiredFields = [
    'TenantName',
    'ConnectionId',
    'IntegrationId',
    'ConnectionTypeName',
    'SysType',
    'Description',
  ];

  React.useEffect(() => {
    let systemRef = base64ToObject(integrationSystem);
    if (systemRef) {
      (async () => {
        setLoading(true);
        const response = await listConnectionTypes({
          isServiceWorker: false,
          systemType: systemRef,
        });
        setConnectionTypes(response?.connectionTypes);
        setLoading(false);
      })();
    }
  }, []);
  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  /**
   * fetch Profile Data From API
   * @param {*} stateData
   */
  const fetchConnectionData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getIntegrationConnection({
        data: refObj,
        isServiceWorker: false,
      });
      const connectionData = response.Payload;
      if (connectionData) {
        setTempState({ ...stateData, dataSet: connectionData, edit: true });
        setFormData(connectionData);
        setTenant(findTenant(connectionData));
        setNoRecord(false);
        if (ValidatePermissions(connectionData)) {
          setDetailsDataModified(true);
        }
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState?.edit || (tenantId && tenantId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchConnectionData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setTenant(findTenant(dataSet));
        if (ValidatePermissions(dataSet)) {
          setDetailsDataModified(true);
        } else {
          setDetailsDataModified(false);
        }
        setShouldUpdate(!shouldUpdate);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  React.useEffect(() => {
    let systemRef = base64ToObject(integrationSystem);
    if (systemRef) {
      setSystem(systemRef);
      let tempTenant = findTenantById(systemRef?.TenantId);
      if (tempTenant) {
        setTenant(tempTenant);
        setFormData((prevState) => ({
          ...prevState,
          TenantId: systemRef?.TenantId,
          TenantName: tempTenant?.TenantName,
          IntegrationId: systemRef?.IntegrationId,
          SysType: systemRef?.SysType,
        }));
      }
    }
  }, []);

  /**
   * on update the tenantID update the formData
   */
  React.useEffect(() => {
    if (!tempState?.edit && tenantId === 'new') {
      let tempTenant = findTenantById(system?.TenantId);
      if (tempTenant) {
        setTenant(tempTenant);
        setFormData((prevState) => ({
          ...prevState,
          TenantId: system?.TenantId,
          TenantName: tempTenant?.TenantName,
        }));
      }
    }
  }, [tenantID]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the connection data to api
   */
  const onSave = async ({ isExit = false, isContinue = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }
    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;

    if (tempState?.edit) {
      method = updateIntegrationConnection;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createIntegrationConnection;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        if (!tempState?.edit) {
          setFormData({
            TenantId: system?.TenantId,
            TenantName: tenant?.TenantName,
            ConnectionId: '',
            SystemConnTypeId: '',
            ConnectionTypeName: '',
            IntegrationId: system?.IntegrationId,
            SysType: system?.SysType,
            Description: '',
            AuthEndpointUrl: '',
            ApiEndpointUrl: '',
            Active: true,
          });
        }
        // Update tempState to trigger rerender
        setTempState({
          ...tempState,
          dataSet: response?.Payload,
        });
        if (isContinue) {
          const payloadData = response?.Payload;
          const refId = objectToBase64({
            IntegrationId: payloadData?.IntegrationId,
            ConnectionId: payloadData?.ConnectionId,
            TenantId: payloadData?.TenantId,
            SysType: payloadData?.SysType,
          });
          navigate(
            `/integration/connection/${payloadData?.TenantId}?refId=${refId}&integrationSystem=${integrationSystem}`,
            {
              state: {
                edit: true,
                dataSet: payloadData,
                integrationSystem: system,
              },
            },
          );
        }
        if (isExit) {
          navigate(
            `/integration/system/${system?.TenantId}?refId=${integrationSystem}`,
            {
              state: {
                edit: true,
                dataSet: system,
              },
            },
          );
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to integrationConnections listing screen
   */
  const onExit = () => {
    navigate(
      `/integration/system/${system?.TenantId}?refId=${integrationSystem}`,
      {
        state: {
          edit: true,
          dataSet: system,
        },
      },
    );
  };

  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  /**
   * delete the integration connection
   */
  const onDelete = async () => {
    setVisible(false);
    setLoading(true);
    let response = await deleteIntegrationConnection({
      data: formData,
      isServiceWorker: false,
    });
    try {
      if (response.IsSuccess) {
        GenerateNotification(
          buildNotification(DELETE_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        navigate(
          `/integration/system/${system?.TenantId}?refId=${integrationSystem}`,
          {
            state: {
              edit: true,
              dataSet: system,
            },
          },
        );
      } else {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}
          {visible && (
            <Dialog
              title={'Please confirm'}
              onClose={() => setVisible(!visible)}
            >
              <p className='dialog-content-msg'>
                Are you sure you want to delete this Integration Connection?
              </p>
              <DialogActionsBar>
                <button
                  className='k-button k-button-md k-rounded-md k-button-solid k-button-solid-base'
                  onClick={() => setVisible(!visible)}
                >
                  No
                </button>
                <button
                  className='k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md'
                  onClick={() => onDelete()}
                >
                  Yes
                </button>
              </DialogActionsBar>
            </Dialog>
          )}
          {noRecord ? (
            <NoRecordFound
              recordNotFoundText={'No Data found'}
              onExit={onExit}
            />
          ) : (
            <>
              <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                <DescriptionTextArea
                  onChange={handleInputChange}
                  label={'Description'}
                  renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                  validateForm={validateForm}
                  formData={formData}
                  setFormData={setFormData}
                  detailsDataModified={detailsDataModified}
                  isTextArea={isTextArea}
                  setIsTextArea={setIsTextArea}
                  editorField={editorField}
                  setEditorField={setEditorField}
                  row={2}
                />
              </div>
              <div
                className={`add-test main-page-section add-page-section ${!isTextArea ? '' : 'k-d-none'}`}
              >
                <Card className='tophead'>
                  <CardHeader>
                    <div className='action-buttons-container'>
                      <span
                        className='action-icon-wrapper !k-d-block'
                        onClick={() => onExit()}
                      >
                        <FontAwesomeIcon
                          className='k-mr-2 k-pt-3'
                          icon={faArrowLeft}
                        />
                      </span>
                      <div>
                        <p className='display-text k-mb-0'>
                          Connection : {formData?.ConnectionId}
                        </p>
                      </div>
                    </div>
                    {formData?.Description && (
                      <div className='desciption-label'>
                        <span className='k-d-block k-mb-2'>
                          {formData?.Description}
                        </span>
                      </div>
                    )}
                  </CardHeader>
                </Card>
                <Card className='k-mt-3 detail-card'>
                  <CardHeader>
                    <h6 className='card-title-secondary'>Details</h6>
                  </CardHeader>
                  <CardBody className='detail-card detail-card-body'>
                    <div>
                      <Form
                        onSubmit={onSave}
                        render={(formRenderProps) => (
                          <div className='card-content'>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <TenantDropdown
                                  textField='TenantName'
                                  label='Tenant'
                                  dataItemKey='TenantId'
                                  className='tenant-dropdown k-align-self-end'
                                  tenant={tenant}
                                  setTenant={setTenant}
                                  defaultItem={{
                                    TenantName: 'Select Tenant ...',
                                    disabled: true,
                                  }}
                                  editMode={'ReadOnly'}
                                  filterable={true}
                                  tenantFormId='TenantId'
                                  tenantFormName='TenantName'
                                  setFormData={setFormData}
                                  requiredField={true}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.IntegrationId) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'IntegrationId'}
                                  id={'IntegrationId'}
                                  name={'IntegrationId'}
                                  data={system?.IntegrationId}
                                  onChange={handleInputChange}
                                  label={'Integration Id'}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={'ReadOnly'}
                                  optional={false}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.IntegrationId,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.SysType) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'SysType'}
                                  id={'SysType'}
                                  name={'SysType'}
                                  data={formData?.SysType}
                                  onChange={handleInputChange}
                                  label={'System Type'}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={'ReadOnly'}
                                  optional={false}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.SysType,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.ConnectionId) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'ConnectionId'}
                                  id={'ConnectionId'}
                                  name={'ConnectionId'}
                                  data={formData?.ConnectionId}
                                  onChange={handleInputChange}
                                  label={'Connection Id'}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    tempState?.edit ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.ConnectionId,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.ConnectionTypeName) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <DropDownInput
                                  label='Connection Type'
                                  textField='ConnectionType'
                                  dataItemKey='SystemConnTypeId'
                                  name={'ConnectionType'}
                                  dataSet={connectionTypes}
                                  value={
                                    formData?.ConnectionTypeName
                                      ? {
                                          ConnectionType:
                                            formData?.ConnectionTypeName,
                                          SystemConnTypeId:
                                            formData?.SystemConnTypeId,
                                        }
                                      : null
                                  }
                                  handleChange={(e) => {
                                    setFormData((prevState) => ({
                                      ...prevState,
                                      ConnectionTypeName:
                                        e.value?.ConnectionType,
                                      SystemConnTypeId:
                                        e.value?.SystemConnTypeId,
                                    }));
                                  }}
                                  defaultItem={{
                                    ConnectionType: 'Select Type ...',
                                  }}
                                  filterable={true}
                                  requiredField={true}
                                  renderTypes={
                                    tempState?.edit ? 'ReadOnly' : ''
                                  }
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.ConnectionTypeName,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper field-wrapper-description field-wrapper-100 ${isValidate(validateForm, formData?.Description) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <DescriptionTextArea
                                  onChange={handleInputChange}
                                  label={'Description'}
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  validateForm={validateForm}
                                  formData={formData}
                                  setFormData={setFormData}
                                  detailsDataModified={detailsDataModified}
                                  isTextArea={isTextArea}
                                  setIsTextArea={setIsTextArea}
                                  editorField={{
                                    name: 'Description',
                                    value: formData?.Description,
                                    label: 'Description',
                                  }}
                                  setEditorField={setEditorField}
                                  row={2}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.Description,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>

                            <div className='k-d-flex single-field-row k-mb-1'>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'AuthEndpointUrl'}
                                  id={'AuthEndpointUrl'}
                                  name={'AuthEndpointUrl'}
                                  data={formData?.AuthEndpointUrl}
                                  onChange={handleInputChange}
                                  label={'Auth Endpoint Url'}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'ApiEndpointUrl'}
                                  id={'ApiEndpointUrl'}
                                  name={'ApiEndpointUrl'}
                                  data={formData?.ApiEndpointUrl}
                                  onChange={handleInputChange}
                                  label={'Api Endpoint Url'}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper k-mt-3`}>
                                <Field
                                  name='Active'
                                  component={InputSwitch}
                                  label='Active'
                                  onLabel='Yes'
                                  offLabel='No'
                                  id='Active'
                                  checked={formData?.Active}
                                  onChange={handleInputChange}
                                  disabled={!detailsDataModified}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </CardBody>
                </Card>
                {detailsDataModified && (
                  <Card className='k-mt-3'>
                    <CardHeader>
                      <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                        <>
                          <Button
                            icon={'cancel'}
                            onClick={() => onExit()}
                            type='button'
                          >
                            Cancel
                          </Button>
                          <SplitButton
                            text='save'
                            themeColor={'primary'}
                            onButtonClick={() =>
                              onSave({ isExit: false, isContinue: true })
                            }
                            onItemClick={handleSaveSplit}
                          >
                            <SplitButtonItem text='Save & Close' />
                          </SplitButton>
                        </>
                      </div>
                    </CardHeader>
                  </Card>
                )}
                {tempState?.edit && (
                  <Card className='k-mt-3 detail-grid-section'>
                    <CardBody className='!k-px-0'>
                      <DesktopLayout
                        handleSelect={handleSelect}
                        selected={selected}
                        detailsDataModified={detailsDataModified}
                        state={tempState}
                        shouldUpdate={shouldUpdate}
                        setShouldUpdate={setShouldUpdate}
                      />
                    </CardBody>
                  </Card>
                )}
              </div>
            </>
          )}
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddIntegrationConnection;
